/**
 * useAnalytics Composable
 * Main interface for manual analytics tracking
 */

import { analyticsService } from '../services/analytics.service'
import type { AnalyticsEventName } from '../types'

export const useAnalytics = () => {
  /**
   * Track a custom event
   * 
   * @example
   * analytics.track('post_created', { postId: '123', postType: 'discussion' })
   */
  const track = (event: AnalyticsEventName | string, properties?: Record<string, any>): void => {
    analyticsService.track(event, properties)
  }

  /**
   * Set global context for all events
   * 
   * @example
   * analytics.setContext({ groupSlug: 'my-group', locationId: 'loc-123' })
   */
  const setContext = (context: Record<string, any>): void => {
    analyticsService.setContext(context)
  }

  /**
   * Update a single context property
   * 
   * @example
   * analytics.updateContext('groupSlug', 'new-group')
   */
  const updateContext = (key: string, value: any): void => {
    analyticsService.updateContext(key, value)
  }

  /**
   * Get current context
   */
  const getContext = (): Record<string, any> => {
    return analyticsService.getContext()
  }

  /**
   * Clear all context
   */
  const clearContext = (): void => {
    analyticsService.clearContext()
  }

  /**
   * Identify a user (notifies all providers)
   * 
   * @example
   * analytics.identify('user-123', { contactId: 'contact-456', email: 'user@example.com' })
   */
  const identify = (userId: string, traits?: Record<string, any>): void => {
    analyticsService.identify(userId, traits)
  }

  return {
    track,
    identify,
    setContext,
    updateContext,
    getContext,
    clearContext,
  }
}

